export const SeasonalityImpact = () => {
  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-block px-4 py-2 bg-teal-50 border border-teal-200 rounded-full mb-6">
            <span className="text-teal-600 text-sm font-semibold">Seasonal Patterns</span>
          </div>
          <h2 className="text-4xl font-bold text-gray-900 mb-6">
            Understanding Seasonality in T12 Data
          </h2>
          <p className="text-xl text-gray-700 max-w-3xl mx-auto">
            Apartment building performance varies by season. T12 captures these fluctuations, giving buyers a complete picture of annual performance cycles.
          </p>
        </div>
        
        <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
          <div>
            <div className="aspect-w-4 aspect-h-3 rounded-2xl overflow-hidden shadow-2xl">
              <img 
                src="https://public.readdy.ai/ai/img_res/35c5d0fd64a77aa750f8a6831fc08046.jpg"
                alt="Seasonal trends chart showing apartment building performance variations"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
          
          <div className="space-y-6">
            <div className="bg-gradient-to-br from-slate-50 to-gray-50 rounded-xl p-6 border border-gray-200">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Why 12 Months Matters</h3>
              <p className="text-gray-700 mb-4">
                Using a full 12-month period ensures you capture all seasonal variations in income and expenses. This prevents misleading valuations based on peak or slow periods.
              </p>
              
              <div className="bg-white rounded-lg p-4 border border-gray-200">
                <h4 className="font-semibold text-gray-900 mb-3">Example: The Danger of Partial Year Data</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-700">Summer 6 months (May-Oct):</span>
                    <span className="font-bold text-green-600">$210,000 NOI</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-700">Annualized (×2):</span>
                    <span className="font-bold text-green-600">$420,000 NOI</span>
                  </div>
                  <div className="flex justify-between items-center border-t border-gray-300 pt-2 mt-2">
                    <span className="text-gray-700">Actual T12 NOI:</span>
                    <span className="font-bold text-gray-900">$360,000 NOI</span>
                  </div>
                  <div className="flex justify-between items-center text-red-600">
                    <span className="font-semibold">Overvaluation:</span>
                    <span className="font-bold">$60,000 (17%)</span>
                  </div>
                </div>
              </div>
              
              <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-gray-700">
                  <strong>Buyer Protection:</strong> Buyers insist on T12 specifically to avoid being misled by seasonal peaks. Trying to use partial-year data will kill your deal.
                </p>
              </div>
            </div>
            
            <div className="bg-gradient-to-br from-slate-50 to-gray-50 rounded-xl p-6 border border-gray-200">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Common Seasonal Patterns</h3>
              
              <div className="space-y-3">
                <div className="bg-white rounded-lg p-4 border border-gray-200">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                      <i className="ri-sun-line text-xl text-green-600"></i>
                    </div>
                    <div>
                      <div className="font-bold text-gray-900">Spring/Summer (Apr-Aug)</div>
                      <div className="text-sm text-gray-600">Peak Leasing Season</div>
                    </div>
                  </div>
                  <ul className="text-sm text-gray-700 space-y-1 ml-13">
                    <li>• Highest demand and occupancy rates</li>
                    <li>• Faster unit turnover and re-leasing</li>
                    <li>• Ability to command higher rents</li>
                    <li>• Lower vacancy loss</li>
                  </ul>
                </div>
                
                <div className="bg-white rounded-lg p-4 border border-gray-200">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                      <i className="ri-leaf-line text-xl text-orange-600"></i>
                    </div>
                    <div>
                      <div className="font-bold text-gray-900">Fall (Sep-Nov)</div>
                      <div className="text-sm text-gray-600">Moderate Activity</div>
                    </div>
                  </div>
                  <ul className="text-sm text-gray-700 space-y-1 ml-13">
                    <li>• Declining but still solid demand</li>
                    <li>• Back-to-school moves stabilize</li>
                    <li>• Maintenance costs may increase</li>
                    <li>• Preparing for winter expenses</li>
                  </ul>
                </div>
                
                <div className="bg-white rounded-lg p-4 border border-gray-200">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <i className="ri-snowy-line text-xl text-blue-600"></i>
                    </div>
                    <div>
                      <div className="font-bold text-gray-900">Winter (Dec-Mar)</div>
                      <div className="text-sm text-gray-600">Slow Season</div>
                    </div>
                  </div>
                  <ul className="text-sm text-gray-700 space-y-1 ml-13">
                    <li>• Lowest demand and leasing activity</li>
                    <li>• Higher vacancy rates possible</li>
                    <li>• Increased heating and snow removal costs</li>
                    <li>• Longer time to fill vacant units</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div className="bg-gradient-to-br from-slate-50 to-gray-50 rounded-2xl p-8 border border-gray-200 mb-12">
          <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">Sample Monthly T12 Breakdown</h3>
          <p className="text-center text-gray-700 mb-8">
            See how a 24-unit apartment building's performance varies month-by-month
          </p>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-gray-300">
                  <th className="text-left py-3 px-3 font-bold text-gray-900">Month</th>
                  <th className="text-right py-3 px-3 font-bold text-gray-900">Rental Income</th>
                  <th className="text-right py-3 px-3 font-bold text-gray-900">Other Income</th>
                  <th className="text-right py-3 px-3 font-bold text-gray-900">Vacancy</th>
                  <th className="text-right py-3 px-3 font-bold text-gray-900">Expenses</th>
                  <th className="text-right py-3 px-3 font-bold text-gray-900">NOI</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                <tr className="bg-blue-50">
                  <td className="py-3 px-3 font-semibold text-gray-900">Jan 2023</td>
                  <td className="text-right py-3 px-3 text-gray-700">$27,360</td>
                  <td className="text-right py-3 px-3 text-gray-700">$1,200</td>
                  <td className="text-right py-3 px-3 text-red-600">-$1,440</td>
                  <td className="text-right py-3 px-3 text-gray-700">$14,850</td>
                  <td className="text-right py-3 px-3 font-bold text-gray-900">$12,270</td>
                </tr>
                <tr className="bg-blue-50">
                  <td className="py-3 px-3 font-semibold text-gray-900">Feb 2023</td>
                  <td className="text-right py-3 px-3 text-gray-700">$27,360</td>
                  <td className="text-right py-3 px-3 text-gray-700">$1,150</td>
                  <td className="text-right py-3 px-3 text-red-600">-$1,440</td>
                  <td className="text-right py-3 px-3 text-gray-700">$14,200</td>
                  <td className="text-right py-3 px-3 font-bold text-gray-900">$12,870</td>
                </tr>
                <tr className="bg-blue-50">
                  <td className="py-3 px-3 font-semibold text-gray-900">Mar 2023</td>
                  <td className="text-right py-3 px-3 text-gray-700">$28,080</td>
                  <td className="text-right py-3 px-3 text-gray-700">$1,250</td>
                  <td className="text-right py-3 px-3 text-red-600">-$720</td>
                  <td className="text-right py-3 px-3 text-gray-700">$13,500</td>
                  <td className="text-right py-3 px-3 font-bold text-gray-900">$15,110</td>
                </tr>
                <tr className="bg-green-50">
                  <td className="py-3 px-3 font-semibold text-gray-900">Apr 2023</td>
                  <td className="text-right py-3 px-3 text-gray-700">$28,800</td>
                  <td className="text-right py-3 px-3 text-gray-700">$1,300</td>
                  <td className="text-right py-3 px-3 text-red-600">$0</td>
                  <td className="text-right py-3 px-3 text-gray-700">$12,800</td>
                  <td className="text-right py-3 px-3 font-bold text-green-700">$17,300</td>
                </tr>
                <tr className="bg-green-50">
                  <td className="py-3 px-3 font-semibold text-gray-900">May 2023</td>
                  <td className="text-right py-3 px-3 text-gray-700">$28,800</td>
                  <td className="text-right py-3 px-3 text-gray-700">$1,350</td>
                  <td className="text-right py-3 px-3 text-red-600">$0</td>
                  <td className="text-right py-3 px-3 text-gray-700">$12,600</td>
                  <td className="text-right py-3 px-3 font-bold text-green-700">$17,550</td>
                </tr>
                <tr className="bg-green-50">
                  <td className="py-3 px-3 font-semibold text-gray-900">Jun 2023</td>
                  <td className="text-right py-3 px-3 text-gray-700">$28,800</td>
                  <td className="text-right py-3 px-3 text-gray-700">$1,400</td>
                  <td className="text-right py-3 px-3 text-red-600">$0</td>
                  <td className="text-right py-3 px-3 text-gray-700">$13,200</td>
                  <td className="text-right py-3 px-3 font-bold text-green-700">$17,000</td>
                </tr>
                <tr className="bg-green-50">
                  <td className="py-3 px-3 font-semibold text-gray-900">Jul 2023</td>
                  <td className="text-right py-3 px-3 text-gray-700">$28,800</td>
                  <td className="text-right py-3 px-3 text-gray-700">$1,450</td>
                  <td className="text-right py-3 px-3 text-red-600">$0</td>
                  <td className="text-right py-3 px-3 text-gray-700">$13,400</td>
                  <td className="text-right py-3 px-3 font-bold text-green-700">$16,850</td>
                </tr>
                <tr className="bg-green-50">
                  <td className="py-3 px-3 font-semibold text-gray-900">Aug 2023</td>
                  <td className="text-right py-3 px-3 text-gray-700">$28,800</td>
                  <td className="text-right py-3 px-3 text-gray-700">$1,400</td>
                  <td className="text-right py-3 px-3 text-red-600">$0</td>
                  <td className="text-right py-3 px-3 text-gray-700">$13,100</td>
                  <td className="text-right py-3 px-3 font-bold text-green-700">$17,100</td>
                </tr>
                <tr className="bg-yellow-50">
                  <td className="py-3 px-3 font-semibold text-gray-900">Sep 2023</td>
                  <td className="text-right py-3 px-3 text-gray-700">$28,800</td>
                  <td className="text-right py-3 px-3 text-gray-700">$1,350</td>
                  <td className="text-right py-3 px-3 text-red-600">$0</td>
                  <td className="text-right py-3 px-3 text-gray-700">$13,300</td>
                  <td className="text-right py-3 px-3 font-bold text-gray-900">$16,850</td>
                </tr>
                <tr className="bg-yellow-50">
                  <td className="py-3 px-3 font-semibold text-gray-900">Oct 2023</td>
                  <td className="text-right py-3 px-3 text-gray-700">$28,080</td>
                  <td className="text-right py-3 px-3 text-gray-700">$1,300</td>
                  <td className="text-right py-3 px-3 text-red-600">-$720</td>
                  <td className="text-right py-3 px-3 text-gray-700">$13,600</td>
                  <td className="text-right py-3 px-3 font-bold text-gray-900">$15,060</td>
                </tr>
                <tr className="bg-yellow-50">
                  <td className="py-3 px-3 font-semibold text-gray-900">Nov 2023</td>
                  <td className="text-right py-3 px-3 text-gray-700">$27,360</td>
                  <td className="text-right py-3 px-3 text-gray-700">$1,250</td>
                  <td className="text-right py-3 px-3 text-red-600">-$1,440</td>
                  <td className="text-right py-3 px-3 text-gray-700">$13,800</td>
                  <td className="text-right py-3 px-3 font-bold text-gray-900">$13,370</td>
                </tr>
                <tr className="bg-blue-50">
                  <td className="py-3 px-3 font-semibold text-gray-900">Dec 2023</td>
                  <td className="text-right py-3 px-3 text-gray-700">$27,360</td>
                  <td className="text-right py-3 px-3 text-gray-700">$1,200</td>
                  <td className="text-right py-3 px-3 text-red-600">-$1,440</td>
                  <td className="text-right py-3 px-3 text-gray-700">$14,500</td>
                  <td className="text-right py-3 px-3 font-bold text-gray-900">$12,620</td>
                </tr>
                <tr className="border-t-2 border-gray-400 bg-teal-50">
                  <td className="py-4 px-3 font-bold text-gray-900">T12 TOTAL</td>
                  <td className="text-right py-4 px-3 font-bold text-gray-900">$338,400</td>
                  <td className="text-right py-4 px-3 font-bold text-gray-900">$15,600</td>
                  <td className="text-right py-4 px-3 font-bold text-red-600">-$7,200</td>
                  <td className="text-right py-4 px-3 font-bold text-gray-900">$162,850</td>
                  <td className="text-right py-4 px-3 font-bold text-teal-600 text-lg">$183,950</td>
                </tr>
              </tbody>
            </table>
          </div>
          
          <div className="mt-6 grid md:grid-cols-3 gap-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="text-sm text-gray-600 mb-1">Peak Season Average (Apr-Aug)</div>
              <div className="text-2xl font-bold text-green-600">$17,160/mo</div>
            </div>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="text-sm text-gray-600 mb-1">Shoulder Season Average (Sep-Nov)</div>
              <div className="text-2xl font-bold text-yellow-600">$15,093/mo</div>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="text-sm text-gray-600 mb-1">Slow Season Average (Dec-Mar)</div>
              <div className="text-2xl font-bold text-blue-600">$13,063/mo</div>
            </div>
          </div>
        </div>
        
        <div className="bg-teal-50 border-l-4 border-teal-500 rounded-r-lg p-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <i className="ri-lightbulb-line text-2xl text-teal-600"></i>
            </div>
            <div>
              <h4 className="font-bold text-gray-900 mb-2">Strategic Timing Tip</h4>
              <p className="text-gray-700">
                If possible, time your sale so your T12 period ends during or just after peak season. A T12 ending in August will show stronger performance than one ending in February, even for the same property. This can impact your sale price by 5-10%.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};